import { useEffect, useReducer } from "react";
import { ErrorDetailContainer } from "../api/config";

export type TState<T> = {
    data: T | null;
    isLoading: boolean;
    error: any | null;
    firstTimeLoading: boolean; // indicated that this is the initial fetch request
};

export interface QueryResult<T> {
    data: T | null;
    isLoading: boolean;
    error: ErrorDetailContainer | null;
    firstTimeLoading: boolean;
    reFetch: () => void;
}

type TAction<T> =
    | {
          type: "reFetch";
      }
    | {
          type: "setResults";
          payload: {
              data: T;
          };
      }
    | {
          type: "setError";
          payload: any;
      };

const reducer = <T>(state: TState<T>, action: TAction<T>) => {
    switch (action.type) {
        case "reFetch":
            return {
                ...state,
                isLoading: true,
                error: null,
            };
        case "setResults":
            return {
                ...state,
                data: action.payload.data,
                isLoading: false,
                firstTimeLoading: false,
            };
        case "setError":
            return {
                ...state,
                data: null,
                isLoading: false,
                error: action.payload,
            };
        default:
            return { ...state };
    }
};

const useFetchQuery = <T>(
    loader: (...arg: any[]) => Promise<T>
): QueryResult<T> => {
    const [state, dispatch] = useReducer(reducer<T>, {
        data: null,
        isLoading: true,
        error: null,
        firstTimeLoading: true,
    });

    //Fetchs data for the first time.
    //In this level our result section of the state is still empty
    const fetchData = async (offset: number) => {
        dispatch({ type: "reFetch" });
        try {
            const result = await loader(0);

            dispatch({
                type: "setResults",
                payload: {
                    data: result,
                },
            });
        } catch (error) {
            dispatch({ type: "setError", payload: error });
        }
    };

    useEffect(() => {
        fetchData(0);
    }, []);

    const reFetchData = () => {
        fetchData(0);
    };

    return {
        data: state.data,
        isLoading: state.isLoading,
        error: state.error,
        reFetch: reFetchData,
        firstTimeLoading: state.firstTimeLoading,
    };
};

export default useFetchQuery;
